import React, { useEffect, useState, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { CampaignCard } from "../components/CampaignCard";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Search, Plus, Loader2, Link2, PenLine } from "lucide-react";
import { toast } from "sonner";

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("recent");
  const [platforms, setPlatforms] = useState([]);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns", {
        params: { search, platform, status, sort },
      });
      setCampaigns(data.campaigns || []);
    } catch {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, [search, platform, status, sort]);

  useEffect(() => {
    api.get("/platforms").then((r) => setPlatforms(r.data.platforms || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggleSave = async (c) => {
    try {
      if (c.is_saved) await api.delete(`/campaign/${c.id}/save`);
      else await api.post(`/campaign/${c.id}/save`);
      setCampaigns((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_saved: !x.is_saved } : x)));
      toast.success(c.is_saved ? "Removed from saved" : "Campaign saved");
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-mono uppercase tracking-widest mb-2">
              Free Discovery
            </div>
            <h1 className="font-display text-3xl font-bold">Discover Campaigns</h1>
            <p className="text-zinc-500 mt-1">Browse, save and import campaigns — no subscription needed.</p>
          </div>
          <ImportDialog open={importOpen} setOpen={setImportOpen} platforms={platforms} onImported={load} />
        </div>

        {/* filters */}
        <div className="bg-[#141419] border border-white/10 rounded-2xl p-4 mb-6 flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="search-campaigns-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search campaigns, brands, requirements..."
              className="bg-zinc-900 border-white/10 pl-9"
            />
          </div>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger data-testid="filter-platform" className="w-full lg:w-44 bg-zinc-900 border-white/10">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="all">All platforms</SelectItem>
              {platforms.map((p) => (
                <SelectItem key={p.platform_id} value={p.platform_id}>{p.platform_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status" className="w-full lg:w-40 bg-zinc-900 border-white/10">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="ending_soon">Ending soon</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="filter-sort" className="w-full lg:w-40 bg-zinc-900 border-white/10">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="recent">Most recent</SelectItem>
              <SelectItem value="payout">Highest payout</SelectItem>
              <SelectItem value="deadline">Deadline</SelectItem>
              <SelectItem value="name">Name</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 rounded-2xl bg-[#141419] border border-white/5 shimmer" />
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-20 text-zinc-500" data-testid="no-campaigns">
            No campaigns match your filters. Try importing one.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="campaigns-grid">
            {campaigns.map((c) => (
              <CampaignCard key={c.id} campaign={c} onToggleSave={toggleSave} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ImportDialog({ open, setOpen, platforms, onImported }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState({ name: "", platform_id: "generic", external_url: "", brand: "", category: "", payout: "", deadline: "", rules_text: "" });

  const importUrl = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/campaign/import", { url });
      toast.success(`Imported: ${data.name}`);
      setOpen(false);
      setUrl("");
      onImported();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const importManual = async () => {
    if (!manual.name.trim()) { toast.error("Campaign name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/campaign/import/manual", manual);
      toast.success(`Imported: ${data.name}`);
      setOpen(false);
      onImported();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-import-btn" className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold">
          <Plus className="w-4 h-4 mr-1.5" /> Import Campaign
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Import a Campaign (Free)</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="url">
          <TabsList className="bg-zinc-900 border border-white/10">
            <TabsTrigger value="url" data-testid="import-tab-url"><Link2 className="w-4 h-4 mr-1.5" /> By URL</TabsTrigger>
            <TabsTrigger value="manual" data-testid="import-tab-manual"><PenLine className="w-4 h-4 mr-1.5" /> Manual</TabsTrigger>
          </TabsList>
          <TabsContent value="url" className="space-y-3 mt-4">
            <p className="text-sm text-zinc-500">Paste a public campaign URL. We detect the platform and extract requirements.</p>
            <Input
              data-testid="import-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://whop.com/campaign/123"
              className="bg-zinc-900 border-white/10"
            />
            <Button onClick={importUrl} disabled={busy} data-testid="import-url-submit" className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import & Parse Requirements"}
            </Button>
          </TabsContent>
          <TabsContent value="manual" className="space-y-3 mt-4">
            <p className="text-sm text-zinc-500">Platform can't be read automatically? Enter the details and rules yourself.</p>
            <div>
              <Label className="text-zinc-300 text-xs">Campaign name *</Label>
              <Input data-testid="manual-name" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} className="bg-zinc-900 border-white/10 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300 text-xs">Brand</Label>
                <Input value={manual.brand} onChange={(e) => setManual({ ...manual, brand: e.target.value })} className="bg-zinc-900 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Category</Label>
                <Input value={manual.category} onChange={(e) => setManual({ ...manual, category: e.target.value })} className="bg-zinc-900 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Payout</Label>
                <Input value={manual.payout} onChange={(e) => setManual({ ...manual, payout: e.target.value })} placeholder="$2 / 1K views" className="bg-zinc-900 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Deadline</Label>
                <Input value={manual.deadline} onChange={(e) => setManual({ ...manual, deadline: e.target.value })} placeholder="2026-08-30" className="bg-zinc-900 border-white/10 mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">Campaign rules (natural language)</Label>
              <Textarea
                data-testid="manual-rules"
                value={manual.rules_text}
                onChange={(e) => setManual({ ...manual, rules_text: e.target.value })}
                placeholder="Clips 20-45s, vertical 9:16, must mention the brand, add watermark..."
                rows={4}
                className="bg-zinc-900 border-white/10 mt-1"
              />
            </div>
            <Button onClick={importManual} disabled={busy} data-testid="manual-submit" className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import & Parse Requirements"}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
